import { useRef, useEffect } from "react";
import { X } from "lucide-react";

interface TermsAndConditionsProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TermsAndConditions({
  isOpen,
  onClose,
}: TermsAndConditionsProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div
        ref={modalRef}
        className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[90vh]">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Terms and Conditions
            </h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="prose max-w-none text-gray-700 space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                1. Account Registration
              </h2>
              <p className="mb-3">
                By creating an account with AirArrange, you agree to the
                following terms:
              </p>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  You must provide accurate, current, and complete information
                  during registration
                </li>
                <li>
                  You are responsible for maintaining the confidentiality of
                  your account credentials
                </li>
                <li>
                  You must be at least 18 years of age to create an account
                </li>
                <li>
                  One person may not maintain more than one account without
                  prior written consent
                </li>
                <li>
                  You agree to notify us immediately of any unauthorized use of
                  your account
                </li>
                <li>
                  By creating an account, you acknowledge that AirArrange
                  operates a digital marketplace and does not act as a real
                  estate agent, broker, or property manager.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                2. Platform Role
              </h2>
              <p className="mb-3">
                AirArrange is an online marketplace that enables users to
                discover and book workspaces listed by third‑party hosts.
              </p>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  AirArrange does not own, lease, operate, or manage any
                  workspace listed on the Platform
                </li>
                <li>
                  AirArrange does not act as an agent, broker, or representative
                  for any host or user
                </li>
                <li>
                  All bookings form a direct contractual relationship between
                  the user and the host
                </li>
                <li>
                  AirArrange does not negotiate prices, booking terms, or
                  conditions on behalf of any party
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                3. Personal Information and Privacy
              </h2>
              <p className="mb-3">
                We are committed to protecting your personal information in
                accordance with applicable privacy laws:
              </p>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  We collect personal information only as necessary to provide
                  our services
                </li>
                <li>
                  Your personal data is processed in accordance with GDPR, CCPA,
                  and other applicable privacy regulations
                </li>
                <li>
                  We do not sell, rent, or lease your personal information to
                  third parties
                </li>
                <li>
                  You have the right to access, modify, or delete your personal
                  information
                </li>
                <li>
                  We implement appropriate security measures to protect your
                  sensitive information
                </li>
                <li>
                  Payment information is encrypted and processed through secure
                  payment gateways
                </li>
                <li>
                  We may use cookies and similar technologies to enhance your
                  experience
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                4. Pricing and Bookings
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  All workspace prices are set by the host and displayed on the
                  Platform
                </li>
                <li>
                  Prices are fixed at the time of booking and are not negotiable
                  through the Platform
                </li>
                <li>
                  Hosts may update their prices at any time prior to a booking
                  being made
                </li>
                <li>
                  A booking is confirmed only once payment is successfully
                  completed
                </li>
                <li>
                  AirArrange may apply a clearly disclosed platform service fee
                  at checkout
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                5. Payments and Fees
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  Payments are processed through third‑party payment providers
                </li>
                <li>
                  Any applicable platform service fees will be disclosed prior
                  to payment
                </li>
                <li>
                  Service fees are charged for access to and use of the Platform
                </li>
                <li>
                  Hosts are responsible for setting prices and complying with
                  tax obligations
                </li>
                <li>
                  You are responsible for any taxes applicable to your booking
                  unless stated otherwise
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                6. Cancellations and Changes
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  Each booking is subject to the cancellation policy set by the
                  host
                </li>
                <li>
                  Cancellation terms are displayed prior to booking confirmation
                </li>
                <li>AirArrange does not override host cancellation policies</li>
                <li>
                  Refund eligibility is determined by the applicable
                  cancellation policy
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                7. Liability and Disclaimers
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  Our services are provided "as is" without warranties of any
                  kind
                </li>
                <li>
                  We are not liable for damages arising from your use of our
                  services
                </li>
                <li>
                  We do not guarantee the availability or quality of workspace
                  providers
                </li>
                <li>
                  You agree to indemnify us against claims arising from your use
                  of our services
                </li>
                <li>
                  Our total liability is limited to the amount paid for the
                  specific service
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                8. Data Security and Compliance
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  We employ industry-standard security measures to protect your
                  data
                </li>
                <li>
                  Data breaches will be reported in accordance with applicable
                  laws
                </li>
                <li>
                  We comply with relevant data protection regulations in your
                  jurisdiction
                </li>
                <li>
                  Cross-border data transfers are conducted with appropriate
                  safeguards
                </li>
                <li>
                  Regular security audits are performed to maintain data
                  integrity
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                9. Modifications and Termination
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  We reserve the right to modify these terms at any time with
                  notice
                </li>
                <li>
                  Continued use of our services constitutes acceptance of
                  modified terms
                </li>
                <li>
                  You may terminate your account at any time through your
                  account settings
                </li>
                <li>We may terminate accounts for violations of these terms</li>
                <li>
                  Upon termination, certain provisions of these terms will
                  continue to apply
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                8. Contact Information
              </h2>
              <p>
                If you have questions about these terms and conditions, please
                contact us at:
              </p>
              <ul className="space-y-2 list-disc pl-6 mt-3">
                <li>Email: legal@airarrange.com</li>
                <li>Phone: +1 (555) 123-4567</li>
                <li>
                  Address: 123 Business Street, Suite 100, City, State 12345
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                9. Workspace Responsibility
              </h2>
              <ul className="space-y-2 list-disc pl-6">
                <li>
                  Hosts are solely responsible for the accuracy of workspace
                  listings
                </li>
                <li>
                  Hosts are responsible for ensuring compliance with safety and
                  legal requirements
                </li>
                <li>
                  AirArrange does not guarantee the condition, suitability, or
                  legality of any workspace
                </li>
                <li>
                  Any disputes regarding a workspace must be resolved directly
                  with the host
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                10. Governing Law
              </h2>
              <p>
                These Terms are governed by the laws of New South Wales,
                Australia. Any disputes will be subject to the exclusive
                jurisdiction of the courts of New South Wales.
              </p>
            </section>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                By creating an account, you acknowledge that you have read,
                understood, and agree to be bound by these Terms and Conditions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
